import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductService } from '@admin-core/services/product.service';
import { BrandService } from '@admin-core/services/brand.service';
import { CategoryService } from '@admin-core/services/category.service';
import { FilterService } from '@admin-core/services/filter.service';
import { SpecificationDefinitionService } from '@admin-core/services/specification-definition.service';
import {
  ProductAttributeValue,
  ProductDocument,
  ProductImage,
  ProductListItem,
  SpecificationDefinition
} from '@admin-core/models/product.model';
import { Brand } from '@admin-core/models/brand.model';
import { Category } from '@admin-core/models/category.model';
import { FilterValueType } from '@admin-core/models/filter.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { formatMoney } from '@admin-shared/utils/money.util';
import { slugify } from '@admin-shared/utils/slug.util';
import { ImgFallbackDirective } from '@admin-shared/directives/img-fallback.directive';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

interface AttributeField {
  filterId: string;
  name: string;
  valueType: FilterValueType;
  value: string;
  options: { value: string; display: string }[];
}

interface SpecDraft {
  specificationDefinitionId: string | null;
  name: string;
  value: string;
}

interface OptionValueDraft {
  id: string;
  value: string;
}

interface OptionDraft {
  id: string;
  name: string;
  code: string;
  values: OptionValueDraft[];
}

interface VariantDraft {
  id: string;
  sku: string;
  price: number;
  compareAtPrice: number | null;
  isActive: boolean;
  optionValueId: string | null;
}

function newClientId(): string {
  return crypto.randomUUID();
}

@Component({
  selector: 'app-products-admin',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, TranslatePipe, ImgFallbackDirective],
  templateUrl: './products-admin.component.html',
  styleUrl: './products-admin.component.scss'
})
export class ProductsAdminComponent implements OnInit {
  private readonly api = inject(ProductService);
  private readonly brandsApi = inject(BrandService);
  private readonly categoriesApi = inject(CategoryService);
  private readonly filtersApi = inject(FilterService);
  private readonly specsApi = inject(SpecificationDefinitionService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<ProductListItem[]>([]);
  readonly brands = signal<Brand[]>([]);
  readonly leafCategories = signal<Category[]>([]);
  readonly categoryOptions = signal<{ id: string; label: string }[]>([]);
  readonly brandOptions = signal<{ id: string; name: string }[]>([]);
  readonly attributeFields = signal<AttributeField[]>([]);
  readonly categoryTags = signal<SpecificationDefinition[]>([]);
  readonly specDrafts = signal<SpecDraft[]>([]);
  readonly images = signal<ProductImage[]>([]);
  readonly documents = signal<ProductDocument[]>([]);
  readonly docTitle = signal('');
  readonly pendingDocument = signal<File | null>(null);
  readonly loading = signal(false);
  readonly dialogOpen = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly mediaBusy = signal(false);
  readonly filterBrandId = signal('');
  readonly filterCategoryId = signal('');
  readonly optionDrafts = signal<OptionDraft[]>([]);
  readonly variantDrafts = signal<VariantDraft[]>([]);
  readonly optionName = signal('');
  readonly newVariantValue = signal('');
  readonly newVariantSku = signal('');
  readonly newVariantPrice = signal<number | null>(null);
  readonly newVariantCompareAt = signal<number | null>(null);
  readonly newVariantActive = signal(true);

  readonly relatedProducts = signal<ProductListItem[]>([]);
  readonly relatedSearch = signal('');
  readonly relatedSearchResults = computed(() => {
    const query = this.relatedSearch().trim().toLowerCase();
    if (!query || query.length < 2) return [];
    const currentId = this.editingId();
    const alreadyIds = new Set(this.relatedProducts().map((p) => p.id));
    return this.items()
      .filter(
        (p) =>
          p.id !== currentId &&
          !alreadyIds.has(p.id) &&
          (p.name.toLowerCase().includes(query) || p.sku.toLowerCase().includes(query))
      )
      .slice(0, 8);
  });

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    slug: ['', [Validators.required, Validators.maxLength(200)]],
    model: [''],
    brandId: ['', Validators.required],
    categoryId: ['', Validators.required],
    description: [''],
    isActive: [true]
  });

  readonly brandName = computed(() => {
    const map = new Map(this.brands().map((b) => [b.id, b.name]));
    return (id: string) => map.get(id) ?? '—';
  });

  ngOnInit(): void {
    this.brandsApi.getAll().subscribe({
      next: (items) => this.brands.set(items),
      error: () => this.brands.set([])
    });

    this.categoriesApi.getTree().subscribe({
      next: (tree) => {
        const leaves: Category[] = [];
        for (const parent of tree) {
          for (const child of parent.children ?? []) {
            leaves.push(child);
          }
        }
        this.leafCategories.set(leaves);
      },
      error: () => this.leafCategories.set([])
    });

    this.loadFilterOptions();
    this.load();
  }

  onFilterChange(): void {
    this.load();
  }

  private loadFilterOptions(): void {
    this.api.getFilterOptions().subscribe({
      next: (opts) => {
        this.categoryOptions.set(
          (opts.categories ?? []).map((c) => ({
            id: c.id,
            label: c.parentName ? `${c.parentName} / ${c.name}` : c.name
          }))
        );
        this.brandOptions.set(
          (opts.brands ?? []).map((b) => ({ id: b.id, name: b.name }))
        );
      },
      error: () => {
        this.categoryOptions.set([]);
        this.brandOptions.set([]);
      }
    });
  }

  onCategoryChange(): void {
    const categoryId = this.form.controls.categoryId.value;
    if (categoryId) {
      this.loadAttributeFields(categoryId);
      this.loadCategoryTags(categoryId);
      this.specDrafts.set([]);
    } else {
      this.attributeFields.set([]);
      this.categoryTags.set([]);
      this.specDrafts.set([]);
    }
  }

  money(value: number): string {
    return formatMoney(value);
  }

  load(): void {
    this.loading.set(true);
    this.api
      .getPaged({
        page: 1,
        pageSize: 100,
        brandId: this.filterBrandId() || undefined,
        categoryId: this.filterCategoryId() || undefined
      })
      .subscribe({
        next: (res) => {
          this.items.set(res.items);
          this.loading.set(false);
        },
        error: (err) => {
          this.showError(err?.error?.error ?? this.i18n.t('products.loadError'));
          this.loading.set(false);
        }
      });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.images.set([]);
    this.documents.set([]);
    this.specDrafts.set([]);
    this.relatedProducts.set([]);
    this.relatedSearch.set('');
    this.docTitle.set('');
    this.pendingDocument.set(null);
    this.optionDrafts.set([]);
    this.variantDrafts.set([this.newDefaultVariant()]);
    this.resetVariantEditor();
    this.form.reset({
      name: '',
      slug: '',
      model: '',
      brandId: this.brands()[0]?.id ?? '',
      categoryId: this.leafCategories()[0]?.id ?? '',
      description: '',
      isActive: true
    });
    this.dialogOpen.set(true);
    const categoryId = this.form.controls.categoryId.value;
    if (categoryId) {
      this.loadAttributeFields(categoryId);
      this.loadCategoryTags(categoryId);
    } else {
      this.attributeFields.set([]);
      this.categoryTags.set([]);
    }
  }

  openEdit(item: ProductListItem): void {
    this.editingId.set(item.id);
    this.api.getById(item.id).subscribe({
      next: (product) => {
        this.form.setValue({
          name: product.name,
          slug: product.slug,
          model: product.model ?? '',
          brandId: product.brandId,
          categoryId: product.categoryId,
          description: product.description,
          isActive: product.isActive
        });
        const options = this.consolidateOptionsByName(
          (product.options ?? []).map((o) => ({
            id: o.id,
            name: o.name,
            code: o.code,
            values: (o.values ?? []).map((v) => ({ id: v.id, value: v.value }))
          }))
        ).slice(0, 1);
        this.optionDrafts.set(options);
        this.optionName.set(options[0]?.name ?? '');
        const variants = (product.variants ?? [])
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((v) => ({
            id: v.id,
            sku: v.sku,
            price: v.price,
            compareAtPrice: v.compareAtPrice,
            isActive: v.isActive,
            optionValueId: v.optionValueId ?? null
          }));
        this.variantDrafts.set(variants.length > 0 ? variants : [this.newDefaultVariant()]);
        this.resetVariantEditor();
        this.images.set(product.images ?? []);
        this.documents.set(product.documents ?? []);
        this.relatedSearch.set('');
        this.relatedProducts.set([...(product.relatedProducts ?? [])]);
        this.specDrafts.set(
          (product.specifications ?? []).map((s) => ({
            specificationDefinitionId: s.specificationDefinitionId,
            name: s.name,
            value: s.value
          }))
        );
        this.docTitle.set('');
        this.dialogOpen.set(true);
        this.loadAttributeFields(product.categoryId, product.attributes);
        this.loadCategoryTags(product.categoryId);
      },
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('products.loadError'))
    });
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.attributeFields.set([]);
    this.categoryTags.set([]);
    this.specDrafts.set([]);
    this.relatedProducts.set([]);
    this.relatedSearch.set('');
    this.images.set([]);
    this.documents.set([]);
    this.docTitle.set('');
    this.pendingDocument.set(null);
    this.optionDrafts.set([]);
    this.variantDrafts.set([]);
    this.resetVariantEditor();
  }

  onNameChange(value: string): void {
    if (!this.editingId()) {
      this.form.controls.slug.setValue(slugify(value));
    }
  }

  setAttributeValue(filterId: string, value: string): void {
    this.attributeFields.update((fields) =>
      fields.map((f) => (f.filterId === filterId ? { ...f, value } : f))
    );
  }

  addSpecRow(): void {
    this.specDrafts.update((rows) => [
      ...rows,
      { specificationDefinitionId: null, name: '', value: '' }
    ]);
  }

  removeSpecRow(index: number): void {
    this.specDrafts.update((rows) => rows.filter((_, i) => i !== index));
  }

  setSpecTag(index: number, tagName: string): void {
    const trimmed = tagName.trim();
    const tag = this.categoryTags().find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase()
    );
    this.specDrafts.update((rows) =>
      rows.map((row, i) =>
        i === index
          ? {
              ...row,
              specificationDefinitionId: tag?.id ?? null,
              name: tag?.name ?? tagName
            }
          : row
      )
    );
  }

  setSpecValue(index: number, value: string): void {
    this.specDrafts.update((rows) =>
      rows.map((row, i) => (i === index ? { ...row, value } : row))
    );
  }

  onImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    const productId = this.editingId();
    if (!productId || files.length === 0) {
      return;
    }

    // Local previews while upload completes.
    const previews: ProductImage[] = files.map((file, index) => ({
      id: `local-${Date.now()}-${index}`,
      url: URL.createObjectURL(file),
      sortOrder: this.images().length + index,
      isPrimary: this.images().length === 0 && index === 0
    }));
    this.images.update((current) => [...current, ...previews]);

    this.mediaBusy.set(true);
    this.api.uploadImages(productId, files).subscribe({
      next: (uploaded) => {
        for (const preview of previews) {
          URL.revokeObjectURL(preview.url);
        }
        this.images.update((current) => [
          ...current.filter((img) => !String(img.id).startsWith('local-')),
          ...uploaded
        ]);
        this.mediaBusy.set(false);
      },
      error: (err) => {
        for (const preview of previews) {
          URL.revokeObjectURL(preview.url);
        }
        this.images.update((current) => current.filter((img) => !String(img.id).startsWith('local-')));
        this.showError(err?.error?.error ?? this.i18n.t('products.mediaError'));
        this.mediaBusy.set(false);
      }
    });
  }

  setPrimary(image: ProductImage): void {
    const productId = this.editingId();
    if (!productId) {
      return;
    }
    this.mediaBusy.set(true);
    this.api.setPrimaryImage(productId, image.id).subscribe({
      next: () => this.reloadMedia(productId),
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('products.mediaError'));
        this.mediaBusy.set(false);
      }
    });
  }

  removeImage(image: ProductImage): void {
    const productId = this.editingId();
    if (!productId) {
      return;
    }
    this.mediaBusy.set(true);
    this.api.deleteImage(productId, image.id).subscribe({
      next: () => this.reloadMedia(productId),
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('products.mediaError'));
        this.mediaBusy.set(false);
      }
    });
  }

  onDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    this.pendingDocument.set(file);
    if (file) {
      const baseName = file.name.replace(/\.[^.]+$/, '');
      this.docTitle.set(baseName || file.name);
    }
  }

  uploadPendingDocument(): void {
    const productId = this.editingId();
    const file = this.pendingDocument();
    if (!productId || !file) {
      return;
    }

    const title = this.docTitle().trim() || file.name.replace(/\.[^.]+$/, '') || file.name;
    this.mediaBusy.set(true);
    this.api.uploadDocument(productId, file, title).subscribe({
      next: (doc) => {
        this.documents.update((current) => [...current, doc]);
        this.docTitle.set('');
        this.pendingDocument.set(null);
        this.mediaBusy.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('products.mediaError'));
        this.mediaBusy.set(false);
      }
    });
  }

  clearPendingDocument(): void {
    this.pendingDocument.set(null);
    this.docTitle.set('');
  }

  removeDocument(doc: ProductDocument): void {
    const productId = this.editingId();
    if (!productId) {
      return;
    }
    this.mediaBusy.set(true);
    this.api.deleteDocument(productId, doc.id).subscribe({
      next: () => this.reloadMedia(productId),
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('products.mediaError'));
        this.mediaBusy.set(false);
      }
    });
  }

  addRelatedProduct(item: ProductListItem): void {
    this.relatedProducts.update((list) => [...list, item]);
    this.relatedSearch.set('');
  }

  removeRelatedProduct(id: string): void {
    this.relatedProducts.update((list) => list.filter((p) => p.id !== id));
  }

  setOptionName(name: string): void {
    this.optionName.set(name);
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    const code =
      trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 50) || 'option';
    this.optionDrafts.update((list) => {
      if (list.length === 0) {
        return list;
      }
      return [{ ...list[0], name: trimmed, code: list[0].code || code }];
    });
  }

  onNewVariantValueChange(value: string): void {
    const previous = this.newVariantValue().trim();
    this.newVariantValue.set(value);

    const currentSku = this.newVariantSku().trim();
    const previousSuggested = previous ? this.suggestSku(previous) : '';
    // Keep updating while the SKU still matches the last auto-suggestion (user hasn't edited it).
    const skuIsAuto =
      !currentSku || (previousSuggested !== '' && currentSku === previousSuggested);

    if (!skuIsAuto) {
      return;
    }

    const trimmed = value.trim();
    this.newVariantSku.set(trimmed ? this.suggestSku(trimmed) : '');
  }

  addVariant(): void {
    const optionName = this.optionName().trim();
    const valueLabel = this.newVariantValue().trim();
    const sku = this.newVariantSku().trim().toUpperCase();
    const price = Number(this.newVariantPrice());
    const compareRaw = this.newVariantCompareAt();
    const compareAtPrice =
      compareRaw === null || compareRaw === undefined
        ? null
        : Number(compareRaw);
    const normalizedCompare =
      compareAtPrice === null || Number.isNaN(compareAtPrice) ? null : compareAtPrice;

    if (!optionName) {
      this.showError(this.i18n.t('products.optionNameRequired'));
      return;
    }
    if (!valueLabel || !sku || !(price > 0)) {
      this.showError(this.i18n.t('products.variantsIncomplete'));
      return;
    }
    if (normalizedCompare !== null && !(normalizedCompare > price)) {
      this.showError(this.i18n.t('products.compareAtInvalid'));
      return;
    }

    const existingOption = this.optionDrafts()[0];
    const duplicate = (existingOption?.values ?? []).some(
      (v) => v.value.trim().toLowerCase() === valueLabel.toLowerCase()
    );
    if (duplicate) {
      this.showError(this.i18n.t('products.duplicateOptionValue'));
      return;
    }

    const valueId = newClientId();
    const code =
      optionName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 50) || 'option';

    if (!existingOption) {
      this.optionDrafts.set([
        {
          id: newClientId(),
          name: optionName,
          code,
          values: [{ id: valueId, value: valueLabel }]
        }
      ]);
    } else {
      this.optionDrafts.set([
        {
          ...existingOption,
          name: optionName,
          code: existingOption.code || code,
          values: [...existingOption.values, { id: valueId, value: valueLabel }]
        }
      ]);
    }

    const nextVariant: VariantDraft = {
      id: newClientId(),
      sku,
      price,
      compareAtPrice: normalizedCompare,
      isActive: this.newVariantActive(),
      optionValueId: valueId
    };

    this.variantDrafts.update((list) => {
      const withoutDefault = list.filter((v) => v.optionValueId);
      return [...withoutDefault, nextVariant];
    });

    this.resetVariantEditor({ keepOptionName: true });
  }

  removeVariant(variantId: string): void {
    const variant = this.variantDrafts().find((v) => v.id === variantId);
    if (!variant) return;

    if (variant.optionValueId) {
      this.optionDrafts.update((list) =>
        list
          .map((option) => ({
            ...option,
            values: option.values.filter((v) => v.id !== variant.optionValueId)
          }))
          .filter((option) => option.values.length > 0)
      );
    }

    this.variantDrafts.update((list) => list.filter((v) => v.id !== variantId));

    if (this.optionDrafts().length === 0) {
      this.optionName.set('');
      if (this.variantDrafts().length === 0) {
        this.variantDrafts.set([
          this.newDefaultVariant({
            sku: variant.sku,
            price: variant.price,
            compareAtPrice: variant.compareAtPrice,
            isActive: variant.isActive
          })
        ]);
      }
    }
  }

  /** Same option name twice (e.g. Size/50ml + Size/100ml) → one option with both values. */
  private consolidateOptionsByName(options: OptionDraft[]): OptionDraft[] {
    const merged: OptionDraft[] = [];
    for (const option of options) {
      const key = option.name.trim().toLowerCase();
      const existing = merged.find((o) => o.name.trim().toLowerCase() === key);
      if (!existing) {
        merged.push({
          ...option,
          values: option.values.map((v) => ({ ...v }))
        });
        continue;
      }
      const seen = new Set(existing.values.map((v) => v.value.toLowerCase()));
      for (const value of option.values) {
        if (!seen.has(value.value.toLowerCase())) {
          seen.add(value.value.toLowerCase());
          existing.values.push({ ...value });
        }
      }
    }
    return merged.slice(0, 1);
  }

  private newDefaultVariant(seed?: Partial<VariantDraft>): VariantDraft {
    return {
      id: seed?.id ?? newClientId(),
      sku: seed?.sku ?? '',
      price: seed?.price ?? 0,
      compareAtPrice: seed?.compareAtPrice ?? null,
      isActive: seed?.isActive ?? true,
      optionValueId: seed?.optionValueId ?? null
    };
  }

  private resetVariantEditor(opts?: { keepOptionName?: boolean }): void {
    if (!opts?.keepOptionName) {
      this.optionName.set(this.optionDrafts()[0]?.name ?? '');
    }
    this.newVariantValue.set('');
    this.newVariantSku.set('');
    this.newVariantPrice.set(null);
    this.newVariantCompareAt.set(null);
    this.newVariantActive.set(true);
  }

  private suggestSku(valueLabel: string): string {
    const base = this.productBaseSku();
    const token = valueLabel.replace(/\s+/g, '').toUpperCase();
    return `${base}-${token}`.slice(0, 100);
  }

  private productBaseSku(): string {
    const fromSlug = (this.form.controls.slug.value || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    if (fromSlug) {
      return fromSlug;
    }

    const option = this.optionDrafts()[0];
    const labels = (option?.values ?? [])
      .map((v) => v.value.replace(/\s+/g, '').toUpperCase())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);

    for (const variant of this.variantDrafts()) {
      let sku = variant.sku.trim().toUpperCase().replace(/\s+/g, '');
      if (!sku) continue;
      for (const label of labels) {
        const suffix = `-${label}`;
        if (sku.endsWith(suffix)) {
          sku = sku.slice(0, -suffix.length);
          break;
        }
      }
      if (sku) {
        return sku;
      }
    }

    return 'SKU';
  }

  updateVariantField(
    variantId: string,
    field: 'sku' | 'price' | 'compareAtPrice' | 'isActive',
    value: string | number | boolean | null
  ): void {
    this.variantDrafts.update((list) =>
      list.map((v) => {
        if (v.id !== variantId) return v;
        if (field === 'sku') return { ...v, sku: String(value ?? '') };
        if (field === 'price') return { ...v, price: Number(value) || 0 };
        if (field === 'compareAtPrice') {
          return {
            ...v,
            compareAtPrice: value === null || value === '' ? null : Number(value)
          };
        }
        return { ...v, isActive: Boolean(value) };
      })
    );
  }

  updateVariantValueLabel(variantId: string, label: string): void {
    const variant = this.variantDrafts().find((v) => v.id === variantId);
    if (!variant?.optionValueId) return;
    const trimmed = label.trim();
    this.optionDrafts.update((list) =>
      list.map((option) => ({
        ...option,
        values: option.values.map((v) =>
          v.id === variant.optionValueId ? { ...v, value: trimmed || v.value } : v
        )
      }))
    );
  }

  optionValueLabel(valueId: string): string {
    for (const option of this.optionDrafts()) {
      const found = option.values.find((v) => v.id === valueId);
      if (found) return found.value;
    }
    return valueId;
  }

  variantOptionLabel(variant: VariantDraft): string {
    return variant.optionValueId ? this.optionValueLabel(variant.optionValueId) : '';
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const attributes = this.buildAttributePayload();
    const options = this.optionDrafts();
    const variants = this.variantDrafts();

    if (variants.length === 0 || variants.some((v) => !v.sku.trim() || v.price <= 0)) {
      this.showError(this.i18n.t('products.variantsIncomplete'));
      return;
    }

    const payload: Record<string, unknown> = {
      name: raw.name,
      slug: raw.slug,
      model: raw.model.trim() || null,
      brandId: raw.brandId,
      description: raw.description,
      categoryId: raw.categoryId,
      isActive: raw.isActive,
      attributes,
      options: options.slice(0, 1).map((o, oi) => ({
        id: o.id,
        name: o.name,
        code: o.code,
        position: oi,
        values: o.values.map((v, vi) => ({
          id: v.id,
          value: v.value,
          position: vi
        }))
      })),
      variants: variants.map((v, vi) => ({
        id: v.id,
        sku: v.sku.trim(),
        price: Number(v.price),
        compareAtPrice:
          v.compareAtPrice === null || v.compareAtPrice === undefined
            ? null
            : Number(v.compareAtPrice),
        isActive: v.isActive,
        position: vi,
        optionValueId: v.optionValueId
      }))
    };

    const id = this.editingId();
    const req$ = id ? this.api.update(id, payload as never) : this.api.create(payload as never);

    req$.subscribe({
      next: (product) => {
        this.saveSpecifications(product.id, () => {
          this.saveRelatedProducts(product.id, () => {
            this.closeDialog();
            this.loadFilterOptions();
            this.load();
          });
        });
      },
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('products.saveError'))
    });
  }

  async remove(item: ProductListItem): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('products.deleteConfirm', { name: item.name }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.api.delete(item.id).subscribe({
      next: () => {
        this.loadFilterOptions();
        this.load();
      },
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('products.deleteError'))
    });
  }

  private saveSpecifications(productId: string, done: () => void): void {
    const drafts = this.specDrafts().filter((d) => d.name.trim() && d.value.trim());
    const payload = drafts.map((d, index) => ({
      specificationDefinitionId: d.specificationDefinitionId,
      name: d.specificationDefinitionId ? null : d.name.trim(),
      value: d.value.trim(),
      sortOrder: index
    }));

    this.api.setSpecifications(productId, payload).subscribe({
      next: () => done(),
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('products.saveError'));
        done();
      }
    });
  }

  private saveRelatedProducts(productId: string, done: () => void): void {
    const ids = this.relatedProducts().map((p) => p.id);
    this.api.setRelatedProducts(productId, ids).subscribe({
      next: () => done(),
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('products.saveError'));
        done();
      }
    });
  }

  private reloadMedia(productId: string): void {
    this.api.getById(productId).subscribe({
      next: (product) => {
        this.images.set(product.images ?? []);
        this.documents.set(product.documents ?? []);
        this.mediaBusy.set(false);
      },
      error: () => this.mediaBusy.set(false)
    });
  }

  private loadCategoryTags(categoryId: string): void {
    this.specsApi.getByCategory(categoryId).subscribe({
      next: (tags) => this.categoryTags.set(tags),
      error: () => this.categoryTags.set([])
    });
  }

  private loadAttributeFields(
    categoryId: string,
    existing: ProductAttributeValue[] = []
  ): void {
    this.filtersApi.getCategoryFilters(categoryId, { includeAllOptions: true }).subscribe({
      next: (res) => {
        const byFilterId = new Map(existing.map((a) => [a.filterId, a]));
        const fields = (res.filters ?? [])
          .filter((f) => !this.isSystemManagedFilter(f.name))
          .map((f) => {
            const current = byFilterId.get(f.id);
            return {
              filterId: f.id,
              name: f.name,
              valueType: this.normalizeValueType(f.valueType),
              value: current ? this.attributeToInput(current) : '',
              options: (f.values ?? []).map((v) => ({
                value: String(v.value),
                display: v.display
              }))
            } satisfies AttributeField;
          });
        this.attributeFields.set(fields);
      },
      error: () => this.attributeFields.set([])
    });
  }

  private isSystemManagedFilter(name: string): boolean {
    const key = name.toLowerCase();
    return key === 'brand' || key === 'price';
  }

  private normalizeValueType(value: FilterValueType | number): FilterValueType {
    if (typeof value === 'string') {
      return value;
    }
    const map: FilterValueType[] = ['Guid', 'Text', 'Number', 'Boolean', 'Options'];
    return map[value] ?? 'Text';
  }

  private attributeToInput(attr: ProductAttributeValue): string {
    const type = this.normalizeValueType(attr.valueType as FilterValueType | number);
    switch (type) {
      case 'Guid':
        return attr.guidValue ?? '';
      case 'Number':
        return attr.numberValue == null ? '' : String(attr.numberValue);
      case 'Boolean':
        return attr.booleanValue == null ? '' : String(attr.booleanValue);
      case 'Options':
      case 'Text':
      default:
        return attr.textValue ?? '';
    }
  }

  private buildAttributePayload() {
    return this.attributeFields()
      .filter((f) => f.value.trim() !== '')
      .map((f) => {
        const value = f.value.trim();
        switch (f.valueType) {
          case 'Guid':
            return { filterId: f.filterId, guidValue: value };
          case 'Number':
            return { filterId: f.filterId, numberValue: Number(value) };
          case 'Boolean':
            return {
              filterId: f.filterId,
              booleanValue: value.toLowerCase() === 'true' || value === '1'
            };
          case 'Options':
          case 'Text':
          default:
            return { filterId: f.filterId, textValue: value };
        }
      });
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
